// =======================================================
// CosmicUniverse — vast dark space with twinkling stars and drifting planets
// =======================================================
//
// Tuned for very wide canvases (e.g. 10900×540, ~20:1).
//
// - Star cells are sized in *pixels* via uResolution, so star density and
//   twinkle look identical regardless of aspect ratio. (A normalized grid
//   would yield sub-pixel stars on a 540 px tall canvas.)
// - The vignette is elliptical (scaled by aspect) so wide canvases don't
//   become a single dark slab.
// - Planets are placed in stratified horizontal bins so they spread evenly
//   across the full width instead of clustering at random.

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uTime;
  uniform vec2  uResolution;
  uniform float uStarDensity;
  uniform float uTwinkleSpeed;
  uniform float uDrift;
  uniform float uNebula;
  uniform float uPlanetCount;
  uniform float uPlanetSize;
  uniform vec3  uBgTop;
  uniform vec3  uBgBottom;
  uniform vec3  uNebulaColor;
  uniform vec3  uStarColor;

  ${GLSL_COMMON}

  // Pixel-sized star layer. cellPx is the on-screen size of one star cell.
  // density is the probability that a cell hosts a star.
  float starLayer(vec2 fragPx, vec2 driftPx, float cellPx, float density, float t) {
    vec2 g = (fragPx + driftPx) / cellPx;
    vec2 cell = floor(g);
    vec2 f = fract(g);

    float alive = step(1.0 - density, hash21(cell + 17.3));
    vec2 starPos = 0.2 + 0.6 * hash22(cell);
    float d = length(f - starPos);

    // Star core size in cell-fractions (so radius scales with cellPx).
    float size = mix(0.06, 0.18, hash21(cell + 3.7));
    float core = smoothstep(size, 0.0, d);

    // Twinkle: per-star sinusoid + occasional sharper sparkle pulse
    float phase = hash21(cell + 91.1) * 6.2831;
    float speed = mix(0.5, 2.4, hash21(cell + 5.5));
    float tw = 0.45 + 0.55 * sin(t * speed + phase);
    float sparkle = pow(max(0.0, sin(t * speed * 0.5 + phase * 1.7)), 18.0);

    // Bright stars get a cross-shaped diffraction spike
    float spike = 0.0;
    if (hash21(cell + 12.9) > 0.88) {
      vec2 d2 = f - starPos;
      float sx = smoothstep(0.30, 0.0, abs(d2.y)) * smoothstep(0.45, 0.0, abs(d2.x));
      float sy = smoothstep(0.30, 0.0, abs(d2.x)) * smoothstep(0.45, 0.0, abs(d2.y));
      spike = (sx + sy) * 0.45;
    }

    return alive * (core * tw + sparkle * 0.7 + spike * tw);
  }

  // Procedural planet — soft shaded disc with rim glow.
  // p and center are in aspect-corrected space (y in [-0.5, 0.5]).
  vec3 drawPlanet(vec2 p, vec2 center, float radius, vec3 col, vec3 rimCol, inout float coverMask) {
    vec2 d = p - center;
    float r = length(d);
    if (r > radius * 1.7) return vec3(0.0);

    // Disc + soft edge
    float disc = smoothstep(radius, radius - 0.003, r);
    // Lambert-ish shading from a fixed sun direction (upper-left)
    vec2 lightDir = normalize(vec2(-0.55, 0.65));
    float lambert = clamp(dot(normalize(d / max(radius, 1e-4)), lightDir) * 0.5 + 0.55, 0.0, 1.0);

    // Surface bands using fbm
    vec2 bandUv = (d / radius) * 2.5;
    float bands = fbm(bandUv * vec2(0.6, 2.2) + vec2(uTime * 0.02, 0.0));
    vec3 surface = col * mix(0.55, 1.2, bands) * lambert;

    // Rim atmosphere — bright halo just outside the disc
    float rim = smoothstep(radius * 1.35, radius, r) - smoothstep(radius, radius - 0.005, r);
    rim = max(rim, 0.0);
    vec3 rimGlow = rimCol * rim * 0.95;

    // Outer faint atmosphere
    float halo = smoothstep(radius * 1.7, radius, r) * 0.18;
    vec3 atmo = rimCol * halo;

    coverMask = max(coverMask, disc);
    return surface * disc + rimGlow + atmo;
  }

  vec3 planetColor(float i) {
    vec3 a = vec3(0.55, 0.45, 0.85); // violet
    vec3 b = vec3(0.95, 0.65, 0.35); // amber
    vec3 c = vec3(0.35, 0.75, 0.85); // teal
    vec3 d = vec3(0.85, 0.45, 0.55); // rose
    vec3 e = vec3(0.45, 0.85, 0.55); // jade
    float h = fract(sin(i * 12.9898) * 43758.5453);
    if (h < 0.20) return a;
    if (h < 0.45) return b;
    if (h < 0.65) return c;
    if (h < 0.85) return d;
    return e;
  }

  void main() {
    float aspect = uResolution.x / uResolution.y;
    vec2 uv = vUv;
    // Aspect-corrected centered space (used for planets). Y in [-0.5, 0.5],
    // X in [-aspect/2, aspect/2] — so planet radii stay perceptually round.
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

    // -------- background gradient --------
    vec3 bg = mix(uBgBottom, uBgTop, uv.y);

    // -------- nebula clouds --------
    // Stretch nebula horizontally on wide canvases so cloud features remain
    // readable (don't squish the noise to vertical bars).
    vec2 nUv = vec2(uv.x * aspect, uv.y) * 1.2 + vec2(uTime * 0.006, uTime * 0.003);
    float n = fbm(nUv);
    n = pow(n, 2.2);
    bg += uNebulaColor * n * uNebula;

    // -------- vignette (elliptical, aspect-aware) --------
    // length() of p alone explodes on 20:1 aspect; squash x by 1/aspect so
    // the falloff is roughly elliptical and tracks the canvas shape.
    vec2 vp = vec2(p.x / max(aspect * 0.5, 1.0), p.y);
    float vig = smoothstep(0.9, 0.25, length(vp));
    bg *= mix(0.55, 1.0, vig);

    // -------- star layers (pixel-sized cells, parallax drift) --------
    vec2 fragPx = uv * uResolution;
    // Drift mostly horizontal — wide canvases reward it.
    vec2 driftPx = vec2(uTime * uDrift * 8.0, uTime * uDrift * 1.5);

    float stars = 0.0;
    // Far layer: rare, bigger cells = sparser big stars
    stars += starLayer(fragPx, driftPx * 0.15, 36.0, 0.10 * uStarDensity, uTime * uTwinkleSpeed)        * 1.00;
    stars += starLayer(fragPx, driftPx * 0.45, 22.0, 0.12 * uStarDensity, uTime * uTwinkleSpeed * 1.3)  * 0.80;
    stars += starLayer(fragPx, driftPx * 0.90, 14.0, 0.14 * uStarDensity, uTime * uTwinkleSpeed * 1.7)  * 0.65;
    // Near layer: tiny, dense dust
    stars += starLayer(fragPx, driftPx * 1.60,  9.0, 0.16 * uStarDensity, uTime * uTwinkleSpeed * 2.1)  * 0.50;

    vec3 starCol = uStarColor * stars;

    // -------- planets (stratified across width) --------
    vec3 planetAccum = vec3(0.0);
    float planetCover = 0.0;
    int N = int(clamp(uPlanetCount, 0.0, 12.0));
    float Nf = max(float(N), 1.0);
    for (int i = 0; i < 12; i++) {
      if (i >= N) break;
      float fi = float(i);

      // Stratified bin in x: each planet gets a horizontal slice of full width
      float binCenter = (fi + 0.5) / Nf;             // 0..1
      float jitter = (hash11(fi * 1.37 + 0.1) - 0.5) * 0.7 / Nf; // up to ±35% bin
      float baseX = binCenter + jitter;
      float baseY = hash11(fi * 2.91 + 5.7);
      // Aspect-corrected centered position. Inset 4% from edges.
      vec2 c = vec2(
        (baseX - 0.5) * aspect * 0.92,
        (baseY - 0.5) * 0.85
      );

      // Slow drift — bias horizontal motion with aspect so it reads on wide.
      float ds = 0.05 + hash11(fi * 3.7) * 0.06;
      float dPhase = fi * 1.7;
      c += vec2(
        cos(uTime * 0.04 + dPhase) * ds * sqrt(max(aspect, 1.0)),
        sin(uTime * 0.03 + dPhase) * ds
      );

      // Radius in y-normalized units → consistent on-screen size regardless of aspect.
      float radius = (0.05 + hash11(fi * 7.3) * 0.10) * uPlanetSize;
      vec3 col = planetColor(fi);
      vec3 rim = mix(col, vec3(1.0), 0.35) * 1.2;

      planetAccum += drawPlanet(p, c, radius, col, rim, planetCover);
    }

    // Stars hidden behind planet discs
    starCol *= (1.0 - planetCover);

    vec3 outCol = bg + starCol + planetAccum;
    gl_FragColor = vec4(outCol, 1.0);
  }
`;

export class CosmicUniverse extends Generator {
  constructor() {
    super({
      name: 'Cosmic Universe',
      category: 'ambient',
      schema: {
        starDensity:   { default: 1.0, min: 0.1, max: 3.0,  step: 0.05, label: 'Star density' },
        twinkleSpeed:  { default: 1.0, min: 0.0, max: 4.0,  step: 0.05, label: 'Twinkle speed' },
        drift:         { default: 1.0, min: 0.0, max: 5.0,  step: 0.05, label: 'Drift speed' },
        nebula:        { default: 0.35,min: 0.0, max: 1.5,  step: 0.01, label: 'Nebula intensity' },
        planetCount:   { default: 7,   min: 0,   max: 12,   step: 1,    label: 'Planet count' },
        planetSize:    { default: 1.0, min: 0.3, max: 2.5,  step: 0.05, label: 'Planet size' },
        bgTop:         { default: '#06031a', type: 'color', label: 'Background top' },
        bgBottom:      { default: '#01010a', type: 'color', label: 'Background bottom' },
        nebulaColor:   { default: '#3a2a6a', type: 'color', label: 'Nebula color' },
        starColor:     { default: '#ffffff', type: 'color', label: 'Star color' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime:         { value: 0 },
        uResolution:   { value: new THREE.Vector2(engine.width, engine.height) },
        uStarDensity:  { value: this.v('starDensity') },
        uTwinkleSpeed: { value: this.v('twinkleSpeed') },
        uDrift:        { value: this.v('drift') },
        uNebula:       { value: this.v('nebula') },
        uPlanetCount:  { value: this.v('planetCount') },
        uPlanetSize:   { value: this.v('planetSize') },
        uBgTop:        { value: new THREE.Color(this.v('bgTop')) },
        uBgBottom:     { value: new THREE.Color(this.v('bgBottom')) },
        uNebulaColor:  { value: new THREE.Color(this.v('nebulaColor')) },
        uStarColor:    { value: new THREE.Color(this.v('starColor')) },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (!this.mesh) return;
    this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    switch (key) {
      case 'starDensity':  u.uStarDensity.value = val; break;
      case 'twinkleSpeed': u.uTwinkleSpeed.value = val; break;
      case 'drift':        u.uDrift.value = val; break;
      case 'nebula':       u.uNebula.value = val; break;
      case 'planetCount':  u.uPlanetCount.value = val; break;
      case 'planetSize':   u.uPlanetSize.value = val; break;
      case 'bgTop':        u.uBgTop.value.set(val); break;
      case 'bgBottom':     u.uBgBottom.value.set(val); break;
      case 'nebulaColor':  u.uNebulaColor.value.set(val); break;
      case 'starColor':    u.uStarColor.value.set(val); break;
    }
  }

  update(dt, time) {
    if (this.mesh) this.mesh.material.uniforms.uTime.value = time;
  }
}
